import React, { useEffect, useRef, useState } from 'react';
import './ReelsCarousel.css';

const ReelModal = ({ reel, onClose, onPrev, onNext, hasPrev, hasNext }) => {
  const videoRef = useRef(null);
  const [isMuted, setIsMuted] = useState(false); // Play with sound by default when modal opens
  const [isPlaying, setIsPlaying] = useState(true);

  // Lock body scroll when modal is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  // Handle Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft' && hasPrev) onPrev();
      if (e.key === 'ArrowRight' && hasNext) onNext();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose, onPrev, onNext, hasPrev, hasNext]);

  // Reset play/mute state when reel changes
  useEffect(() => {
    setIsPlaying(true);
    if (videoRef.current) {
      const playPromise = videoRef.current.play();
      if (playPromise !== undefined) {
        playPromise.catch(error => {
          // Ignore AbortError when media is removed or src changes rapidly
          if (error.name === 'AbortError') return;
          console.log("Auto-play prevented", error);
        });
      }
    }
  }, [reel]);

  const togglePlay = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  const handleOverlayClick = (e) => {
    // Only close if clicking the dark overlay, not the video content
    if (e.target.className === 'reel-modal-overlay') {
      onClose();
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  // Render caption with hashtags colored blue
  const renderCaption = (caption) => {
    if (!caption) return "Instagram Reel";
    
    // Clean up SEO brackets at the end
    let text = caption.replace(/\[.*?\]/gs, "");
    // Clean up mangled emojis
    text = text.replace(/(^|\s)\?{3,}(?=\s|\w)/g, "$1");
    text = text.replace(/\?{3,}/g, ""); 
    
    // Split text to find hashtags
    const parts = text.split(/(#[a-zA-Z0-9_]+)/g);
    
    return parts.map((part, index) => {
      if (part.startsWith('#')) {
        return (
          <span key={index} className="reel-modal-hashtag">
            {part}
          </span>
        );
      }
      return <span key={index}>{part}</span>;
    });
  };

  if (!reel) return null;

  return (
    <div className="reel-modal-overlay" onClick={handleOverlayClick}>
      <button className="reel-modal-close" onClick={onClose} aria-label="Close">
        &times;
      </button>

      {hasPrev && (
        <button className="reel-modal-nav-btn reel-modal-nav-left" onClick={onPrev}>
          &#8249;
        </button>
      )}

      <div className="reel-modal-content">
        {/* Left Side: Video */}
        <div className="reel-modal-video-container" onClick={togglePlay}>
          <video
            ref={videoRef}
            src={reel.media_url}
            poster={reel.thumbnail_url}
            autoPlay
            loop
            muted={isMuted}
            playsInline
          />
          {!isPlaying && (
            <div style={{ position: 'absolute', fontSize: '48px', color: 'white', opacity: 0.8, pointerEvents: 'none' }}>
              ▶
            </div>
          )}
          
          <button 
            className="reel-modal-mute-btn"
            onClick={(e) => { e.stopPropagation(); toggleMute(); }}
          >
            {isMuted ? (
              <svg aria-label="Audio is muted" color="#ffffff" fill="#ffffff" height="16" role="img" viewBox="0 0 48 48" width="16"><path clipRule="evenodd" d="M2.5 13.5v21a3 3 0 0 0 3 3h8.33l13.63 11.23c1.78 1.47 4.54.2 4.54-2.1V2.34c0-2.3-2.76-3.57-4.54-2.1L13.83 11.47H5.5a3 3 0 0 0-3 2.03Z" fillRule="evenodd"></path><path clipRule="evenodd" d="M37.7 32.22a2 2 0 1 0 2.82 2.83l4.75-4.75 4.74 4.75a2 2 0 1 0 2.83-2.83l-4.75-4.74 4.75-4.75a2 2 0 0 0-2.83-2.82l-4.74 4.74-4.75-4.74a2 2 0 0 0-2.82 2.82l4.74 4.75-4.75 4.74Z" fillRule="evenodd"></path></svg>
            ) : (
              <svg aria-label="Audio is playing" color="#ffffff" fill="#ffffff" height="16" role="img" viewBox="0 0 48 48" width="16"><path clipRule="evenodd" d="M2.5 13.5v21a3 3 0 0 0 3 3h8.33l13.63 11.23c1.78 1.47 4.54.2 4.54-2.1V2.34c0-2.3-2.76-3.57-4.54-2.1L13.83 11.47H5.5a3 3 0 0 0-3 2.03Zm34.3 22.15a2 2 0 0 0 2.83 2.83 16 16 0 0 0 0-22.62 2 2 0 0 0-2.83 2.82 12 12 0 0 1 0 16.97ZM31.42 29a2 2 0 1 0 2.83 2.83 8.01 8.01 0 0 0 0-11.32 2 2 0 1 0-2.83 2.83 4 4 0 0 1 0 5.66Z" fillRule="evenodd"></path></svg>
            )}
          </button>
        </div>

        {/* Right Side: Info Panel (Instagram Style) */}
        <div className="reel-modal-info">
          
          <div className="reel-modal-header">
            <div className="reel-modal-avatar-placeholder">
              <span className="avatar-initial">P</span>
            </div>
            <div className="reel-modal-header-text">
              <strong>plurojabalpur_ivf</strong>
              <span>Original audio</span>
            </div>
            <a 
              href={reel.permalink} 
              target="_blank" 
              rel="noopener noreferrer"
              className="reel-modal-header-link"
              title="View on Instagram"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="16" x2="12" y2="12"></line><line x1="12" y1="8" x2="12.01" y2="8"></line></svg>
            </a>
          </div>

          <div className="reel-modal-caption-area">
            <div className="reel-modal-caption-content">
              <div className="reel-modal-avatar-placeholder caption-avatar">
                <span className="avatar-initial">P</span>
              </div>
              <div className="reel-modal-caption-text">
                <span className="caption-username">plurojabalpur_ivf</span> 
                {' '}
                {renderCaption(reel.caption)}
              </div>
            </div>
            <div className="reel-modal-date">{formatDate(reel.posted_at)}</div>
          </div>
          
        </div>
      </div>

      {hasNext && (
        <button className="reel-modal-nav-btn reel-modal-nav-right" onClick={onNext}>
          &#8250;
        </button>
      )}
    </div>
  );
};

export default ReelModal;
